import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";
import { Users, DollarSign, ShoppingBag, Award, TrendingUp, Lock } from "lucide-react";

const fmtUsd = (n) => `$${(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="card-flat p-6" data-testid={testid}>
    <div className="flex items-start justify-between mb-4">
      <Icon size={20} className="text-[#C56B52]" strokeWidth={1.5} />
      <div className="font-mono-plex text-[10px] uppercase tracking-[0.18em] text-[#4B534E]">{label}</div>
    </div>
    <div className="font-serif-display text-4xl text-[#1A3626] leading-none">{value}</div>
    {sub && <div className="text-xs text-[#4B534E] mt-2">{sub}</div>}
  </div>
);

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/"); return; }
    if (!user.is_admin) { setError("forbidden"); return; }
    api.get("/admin/kpis")
      .then(({ data }) => setKpis(data))
      .catch((e) => setError(e?.response?.status === 403 ? "forbidden" : "error"));
  }, [user, loading, navigate]);

  if (loading) {
    return <div className="min-h-screen bg-[#F7F6F0] p-12 font-mono-plex text-xs uppercase tracking-[0.3em] text-[#4B534E]">Loading…</div>;
  }

  if (error === "forbidden") {
    return (
      <div className="min-h-screen bg-[#F7F6F0]">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="admin-forbidden">
          <Lock size={40} className="text-[#9E3C3C] mx-auto mb-6" strokeWidth={1.5} />
          <div className="overline mb-3">Restricted</div>
          <h1 className="font-serif-display text-3xl text-[#121614] mb-3">Admin access only.</h1>
          <p className="text-[#4B534E]">This page is locked to the course owner.</p>
        </div>
      </div>
    );
  }

  if (!kpis) {
    return (
      <div className="min-h-screen bg-[#F7F6F0]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-12 font-mono-plex text-xs uppercase tracking-[0.3em] text-[#4B534E]">Loading metrics…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F6F0]" data-testid="admin-dashboard">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="overline mb-2">Owner dashboard</div>
            <h1 className="font-serif-display text-4xl lg:text-5xl text-[#121614] leading-tight">Course KPIs</h1>
          </div>
          <div className="font-mono-plex text-[10px] uppercase tracking-[0.18em] text-[#4B534E]">
            Refreshed {fmtDate(kpis.generated_at)}
          </div>
        </div>

        {/* Top KPI grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E0DCD1] border border-[#E0DCD1] mb-10">
          <StatCard
            icon={DollarSign}
            label="Total revenue"
            value={fmtUsd(kpis.revenue.total_usd)}
            sub={`${fmtUsd(kpis.revenue.last_7d_usd)} in last 7d · ${fmtUsd(kpis.revenue.last_30d_usd)} in 30d`}
            testid="kpi-revenue"
          />
          <StatCard
            icon={ShoppingBag}
            label="Purchases"
            value={kpis.purchases.total}
            sub={`${kpis.purchases.today} today · ${kpis.purchases.last_7d} (7d) · ${kpis.purchases.last_30d} (30d)`}
            testid="kpi-purchases"
          />
          <StatCard
            icon={Users}
            label="Total users"
            value={kpis.users.total}
            sub={`${kpis.users.enrolled} enrolled · ${kpis.users.browsing} browsing`}
            testid="kpi-users"
          />
          <StatCard
            icon={TrendingUp}
            label="Conversion"
            value={`${kpis.users.conversion_rate_pct}%`}
            sub={`signup → enrolment`}
            testid="kpi-conversion"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E0DCD1] border border-[#E0DCD1] mb-12">
          <StatCard icon={Award} label="Certificates issued" value={kpis.certificates_issued} testid="kpi-certs" />
          <StatCard icon={Award} label="Lessons completed" value={kpis.lessons_completed_total} sub="across all students" testid="kpi-lessons" />
          <div className="bg-white p-6 sm:col-span-2">
            <div className="font-mono-plex text-[10px] uppercase tracking-[0.18em] text-[#4B534E] mb-3">Note</div>
            <p className="text-sm text-[#4B534E] leading-relaxed">
              Stripe is currently using the test key — figures show only test transactions. Swap to your live Stripe key in <span className="font-mono-plex">/app/backend/.env</span> to capture real revenue.
            </p>
          </div>
        </div>

        {/* Module completion */}
        <section className="mb-14" data-testid="module-completion-section">
          <div className="overline mb-3">Module completion</div>
          <h2 className="font-serif-display text-2xl text-[#121614] mb-6">Where students drop off</h2>
          <div className="card-flat overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#EFEBE1] text-left">
                <tr>
                  <th className="px-4 py-3 font-mono-plex text-[10px] uppercase tracking-wider text-[#4B534E]">#</th>
                  <th className="px-4 py-3 font-mono-plex text-[10px] uppercase tracking-wider text-[#4B534E]">Module</th>
                  <th className="px-4 py-3 font-mono-plex text-[10px] uppercase tracking-wider text-[#4B534E] text-right">Lessons</th>
                  <th className="px-4 py-3 font-mono-plex text-[10px] uppercase tracking-wider text-[#4B534E] text-right">Completions</th>
                  <th className="px-4 py-3 font-mono-plex text-[10px] uppercase tracking-wider text-[#4B534E] w-1/3">Rate</th>
                </tr>
              </thead>
              <tbody>
                {kpis.modules.map((m) => (
                  <tr key={m.module_id} className="border-t border-[#E0DCD1]" data-testid={`mod-row-${m.module_id}`}>
                    <td className="px-4 py-3 font-mono-plex text-xs text-[#C56B52]">{m.module_number}</td>
                    <td className="px-4 py-3 text-[#121614]">{m.title}</td>
                    <td className="px-4 py-3 text-right text-[#4B534E]">{m.lessons}</td>
                    <td className="px-4 py-3 text-right text-[#4B534E]">{m.completions}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-1.5 bg-[#EFEBE1] overflow-hidden">
                          <div className="h-full bg-[#1A3626]" style={{ width: `${Math.min(m.completion_rate_pct, 100)}%` }} />
                        </div>
                        <span className="font-mono-plex text-xs text-[#4B534E] w-12 text-right">{m.completion_rate_pct}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Recent transactions */}
        <section className="mb-14" data-testid="recent-transactions-section">
          <div className="overline mb-3">Recent transactions</div>
          <h2 className="font-serif-display text-2xl text-[#121614] mb-6">Last 20 checkouts</h2>
          <div className="card-flat overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#EFEBE1] text-left">
                <tr>
                  <th className="px-4 py-3 font-mono-plex text-[10px] uppercase tracking-wider text-[#4B534E]">When</th>
                  <th className="px-4 py-3 font-mono-plex text-[10px] uppercase tracking-wider text-[#4B534E]">Email</th>
                  <th className="px-4 py-3 font-mono-plex text-[10px] uppercase tracking-wider text-[#4B534E] text-right">Amount</th>
                  <th className="px-4 py-3 font-mono-plex text-[10px] uppercase tracking-wider text-[#4B534E]">Status</th>
                </tr>
              </thead>
              <tbody>
                {kpis.recent_transactions.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-[#4B534E] text-sm">No transactions yet.</td></tr>
                ) : kpis.recent_transactions.map((t, i) => (
                  <tr key={t.session_id || i} className="border-t border-[#E0DCD1]" data-testid={`txn-row-${i}`}>
                    <td className="px-4 py-3 text-[#4B534E] font-mono-plex text-xs">{fmtDate(t.created_at)}</td>
                    <td className="px-4 py-3 text-[#121614]">{t.email}</td>
                    <td className="px-4 py-3 text-right text-[#121614]">{fmtUsd(t.amount)}</td>
                    <td className="px-4 py-3">
                      <span className={`font-mono-plex text-[10px] uppercase tracking-wider px-2 py-1 ${
                        t.payment_status === "paid" ? "bg-[#1A3626] text-white" :
                        t.payment_status === "pending" ? "bg-[#EFEBE1] text-[#4B534E]" :
                        "bg-[#9E3C3C] text-white"
                      }`}>{t.payment_status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Recent users */}
        <section data-testid="recent-users-section">
          <div className="overline mb-3">Recent signups</div>
          <h2 className="font-serif-display text-2xl text-[#121614] mb-6">Last 20 users</h2>
          <div className="card-flat overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#EFEBE1] text-left">
                <tr>
                  <th className="px-4 py-3 font-mono-plex text-[10px] uppercase tracking-wider text-[#4B534E]">Joined</th>
                  <th className="px-4 py-3 font-mono-plex text-[10px] uppercase tracking-wider text-[#4B534E]">Name</th>
                  <th className="px-4 py-3 font-mono-plex text-[10px] uppercase tracking-wider text-[#4B534E]">Email</th>
                  <th className="px-4 py-3 font-mono-plex text-[10px] uppercase tracking-wider text-[#4B534E]">Status</th>
                </tr>
              </thead>
              <tbody>
                {kpis.recent_users.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-[#4B534E] text-sm">No signups yet.</td></tr>
                ) : kpis.recent_users.map((u, i) => (
                  <tr key={u.email || i} className="border-t border-[#E0DCD1]" data-testid={`user-row-${i}`}>
                    <td className="px-4 py-3 text-[#4B534E] font-mono-plex text-xs">{fmtDate(u.created_at)}</td>
                    <td className="px-4 py-3 text-[#121614]">{u.name}</td>
                    <td className="px-4 py-3 text-[#4B534E]">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`font-mono-plex text-[10px] uppercase tracking-wider px-2 py-1 ${
                        u.enrolled ? "bg-[#1A3626] text-white" : "bg-[#EFEBE1] text-[#4B534E]"
                      }`}>{u.enrolled ? "Enrolled" : "Browsing"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
